import { useCallback, useEffect, useState } from "react";
import { DASHBOARD_NOTIFICATION_UPDATED_EVENT } from "../utils/dashboardNotificationEvents";
import { odooClient, type DashboardNotification } from "../services/odooClient";

export function useVisibleDashboardNotification() {
  const [settings, setSettings] = useState<DashboardNotification | null>(null);

  const [cacheClearEnabled, setCacheClearEnabled] = useState(false);

  const loadSettings = useCallback(async (mountedRef: { current: boolean }) => {
    try {
      const [data, cacheEnabled] = await Promise.all([
        odooClient.getVisibleDashboardNotification(),
        odooClient.getCacheClearButtonEnabled(),
      ]);
      if (!mountedRef.current) return;
      setSettings(data);
      setCacheClearEnabled(cacheEnabled);
    } catch {
      if (mountedRef.current) {
        setSettings(null);
        setCacheClearEnabled(false);
      }
    }
  }, []);

  useEffect(() => {
    const mountedRef = { current: true };

    loadSettings(mountedRef);
    const interval = window.setInterval(() => loadSettings(mountedRef), 60000);

    const onUpdated = () => {
      loadSettings(mountedRef);
    };
    window.addEventListener(DASHBOARD_NOTIFICATION_UPDATED_EVENT, onUpdated);

    return () => {
      mountedRef.current = false;
      window.clearInterval(interval);
      window.removeEventListener(DASHBOARD_NOTIFICATION_UPDATED_EVENT, onUpdated);
    };
  }, [loadSettings]);

  const reload = useCallback(() => {
    loadSettings({ current: true });
  }, [loadSettings]);

  const bannerNotification =
    settings?.active && settings.message?.trim()
      ? settings
      : null;

  const showClearCacheInBanner = Boolean(bannerNotification && cacheClearEnabled);

  return {
    settings,
    bannerNotification,
    cacheClearEnabled,
    showClearCacheInBanner,
    reload,
  };
}
